import type { CashPositionSummary } from '@/hooks/cashPositionSummaries'
import type {
  CashPositionMultiPeriod,
  CashPositionMultiPeriodColumn,
} from '@/hooks/useCashPositionSummaryMultiPeriod'
import {
  buildBalancesRows,
  buildLocRows,
  buildProjectionRows,
  type SectionTone,
  type SummaryRow,
} from './cashPositionRows'

/**
 * Cash Position Summary as a download: the columns and rows the screen
 * shows for the current filters and period, with plain numbers, so the PDF and
 * XLSX writers render the same report without touching the DOM or the grid.
 */
export interface CashPositionReport {
  asOf: ReportAsOf
  columns: ReportColumn[]
  /** Balances, LOC, then one block per visible period, in screen order. */
  blocks: ReportBlock[]
}

/** The day the report is run, in the parts the screen's "As of" label is made of. */
export interface ReportAsOf {
  /** "October 7, 2026" */
  date: string
  /** "Wednesday" */
  weekday: string
}

export interface ReportColumn {
  key: string
  /** Facility or pack name; "Combined Totals" for the combined column. */
  label: string
  kind: 'combined' | 'pack' | 'facility'
  /**
   * Columns that must stay on one page together: a pack's members and its
   * subtotal share the pack id; the combined column and a standalone facility
   * are groups of their own.
   */
  groupKey: string
}

export interface ReportBlock {
  key: 'balances' | 'loc' | 'remainder' | 'week1' | 'week2' | 'week3' | 'week4'
  rows: ReportRow[]
}

/** One line of a block; mirrors the presentation flags of `SummaryRow`. */
export interface ReportRow {
  kind: 'section' | 'value' | 'total'
  label: string
  tone: SectionTone
  /**
   * Upper-case, strongest weight: BOOK BALANCE, CASH (Book) + CREDIT AVAILABLE
   * and the remainder's ending balance.
   */
  caps?: true
  /** The block's bottom-line ending balance. */
  eow?: true
  /** "Projected Inflows" / "Projected Outflows". */
  subtotal?: true
  /** "Net Projected Cash Flow". */
  netCashFlow?: true
  /** "Bank Balances" and "Remaining Credit Availability". */
  keyBalance?: true
  /** "Book vs Intacct Variance". */
  variance?: true
  /** A detail line under a projected subtotal, set apart in the printed report. */
  projectionDetail?: true
  /** One value per column, aligned with `columns`; empty for a section row. */
  values: (number | null)[]
}

/** Key and label of the combined column; the label follows the report prototype. */
const COMBINED_COLUMN: ReportColumn = {
  key: 'combined',
  label: 'Combined Totals',
  kind: 'combined',
  groupKey: 'combined',
}

/** Inputs of {@link buildCashPositionReport}: exactly what the grid is rendering. */
export interface CashPositionReportInput {
  data: CashPositionMultiPeriod
  /** `shouldShowCombinedTotal(data.columns)` — the same gate the grid uses. */
  showCombinedTotal: boolean
  /** Period indices the grid renders, in order. */
  visiblePeriodIndices: number[]
  asOf: ReportAsOf
}

/**
 * Builds the downloadable report from the data the Cash Position Summary already
 * holds, reading values the way `CashPositionSummaryGrid` does: the balances and
 * LOC blocks from period 0 (they are period-independent), each projection block
 * from its own period index. Column order is the screen's: the combined column
 * first when shown, then the RPC's order.
 */
export function buildCashPositionReport({
  data,
  showCombinedTotal,
  visiblePeriodIndices,
  asOf,
}: CashPositionReportInput): CashPositionReport {
  const columns: ReportColumn[] = [
    ...(showCombinedTotal ? [COMBINED_COLUMN] : []),
    ...data.columns.map(toReportColumn),
  ]

  // One summary list per period, aligned with `columns`.
  const summariesFor = (periodIdx: number): CashPositionSummary[] => [
    ...(showCombinedTotal ? [data.combinedTotal.periods[periodIdx]] : []),
    ...data.columns.map((col) => col.periods[periodIdx]),
  ]

  const toBlock = (key: ReportBlock['key'], rows: SummaryRow[], periodIdx: number): ReportBlock => {
    const summaries = summariesFor(periodIdx)
    return { key, rows: rows.map((row) => toReportRow(row, summaries, key)) }
  }

  return {
    asOf,
    columns,
    blocks: [
      toBlock('balances', buildBalancesRows(), 0),
      toBlock('loc', buildLocRows(), 0),
      ...visiblePeriodIndices.map((idx) => {
        const period = data.periodMeta[idx]
        return toBlock(period.key, buildProjectionRows(period), idx)
      }),
    ],
  }
}

function toReportColumn(col: CashPositionMultiPeriodColumn): ReportColumn {
  return {
    key: col.key,
    label: col.name,
    kind: col.kind,
    // Members and their pack subtotal carry the same `packId`; a standalone
    // facility (null packId) is a group of its own.
    groupKey: col.packId ?? col.key,
  }
}

function toReportRow(row: SummaryRow, summaries: CashPositionSummary[], blockKey: ReportBlock['key']): ReportRow {
  if (row.kind === 'section') {
    return { kind: 'section', label: row.label, tone: row.tone, values: [] }
  }
  const values = summaries.map((s) => row.field(s))
  if (row.kind === 'value') {
    return {
      kind: 'value',
      label: row.label,
      tone: row.tone,
      ...(row.keyBalance ? { keyBalance: true as const } : {}),
      ...(row.projectionDetail ? { projectionDetail: true as const } : {}),
      values,
    }
  }
  // The printed sample upper-cases only the remainder's ending balance; the
  // week blocks' ending balances read as plain running lines there, although
  // the screen shows every ending balance in caps.
  const caps = row.emphasis === 'caps' && !(row.semanticEow && blockKey !== 'remainder')
  return {
    kind: 'total',
    label: row.label,
    tone: row.tone,
    ...(caps ? { caps: true as const } : {}),
    ...(row.semanticEow ? { eow: true as const } : {}),
    ...(row.projectedSubtotal ? { subtotal: true as const } : {}),
    ...(row.netCashFlow ? { netCashFlow: true as const } : {}),
    ...(row.keyBalance ? { keyBalance: true as const } : {}),
    ...(row.variance ? { variance: true as const } : {}),
    values,
  }
}
